import React, { useMemo, useRef, useCallback, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import ReactQuill, { Quill } from "react-quill"; // Import Quill and the editor component
import "react-quill/dist/quill.snow.css"; // Import Quill styles
import "./editor-style.css";
import "./blog-editor-style.css";
import { IoMdSend } from "react-icons/io";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
// #1 import quill-image-uploader
import ImageUploader from "quill-image-uploader";
import {
  imageUploaderFunction,
  quillEditorFormats,
  quillEditorToolbar,
} from "./utils";
import SavingCircle from "./SavingCircle";
import { createLatestOnlySaveQueue } from "./autosaveQueue";
import { API_AUTH_TYPES } from "../../../api/apiConstants";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { API_ROUTES } from "../../../api/apiConstants";
import useDebounce from "../../../hooks/useDebounce";
import { classNames } from "./../../../helpers/classNames";
import ApiRequest from "../../../api/ApiRequest";
import StreamingSpeechToText from "../../ai/Parabot-Interface/components/StreamingSpeechToText";
import DefaultLoading from "../../loading/DefaultLoading";
import { useAppContext } from "../../../context/app-context";
const Link = Quill.import('formats/link');

/**
 * CustomLink - Extends the built-in Quill link format to open external links in a new tab
 * while keeping internal abundowealth.com links in the same tab.
 */
class CustomLink extends Link {
  /**
   * Creates an anchor element for the given href, adding `target="_blank"` and
   * `rel="noopener noreferrer"` for external URLs.
   * @param {string} value - The href value.
   * @returns {HTMLAnchorElement}
   */
  static create(value) {
    const node = super.create(value);
    node.setAttribute('href', value);
    // Check if the href contains "abundowealth.com"
    if (!value.includes("abundowealth.com")) {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    } else {
      node.removeAttribute("target");
      node.removeAttribute("rel");
    }

    return node;
  }
}

Quill.register(CustomLink, true);

//  #2 register module
Quill.register("modules/imageUploader", ImageUploader);

/** Debounce interval in milliseconds before auto-saving editor content (200 ms). */
const SAVE_INTERVAL_MS = 1000 * 0.2;

const isQuillContentEffectivelyEmpty = (content) => (
  !content?.ops || content.ops.every(
    (op) => !op.insert || !String(op.insert).trim()
  )
);

/**
 * QuillTextEditor - Rich-text editor built on React Quill with auto-save, image upload,
 * and optional speech-to-text dictation support.
 *
 * @param {Object} props
 * @param {string} [props.placeholder='Write something here'] - Placeholder text shown in the empty editor.
 * @param {Object} props.options - Editor options: `route`, `auth`, `field`, `is_draft`, `onSuccess`.
 * @param {string|string[]} props.queryKey - React Query key(s) for the Quill document.
 * @param {string|string[]} [props.listQueryKey] - Additional query key(s) to invalidate on draft submit.
 * @param {Function} [props.handleSubmitOnSuccess] - Called with the document ID after a successful draft submit.
 * @param {Function} [props.handleChangeSideEffect] - Called with the Quill Delta on every change.
 * @param {boolean} [props.acceptOutsideChange=false] - When true, syncs external document updates into the editor.
 * @param {Function} [props.setAcceptOutsideChange] - Setter to reset `acceptOutsideChange` after syncing.
 * @param {boolean} [props.autoFocus=false] - Focuses the editor after loading.
 * @param {React.Ref} ref - Exposes a `{ focus }` handle to parent components.
 * @returns {JSX.Element}
 */
const QuillTextEditor = forwardRef(function QuillTextEditor({
  placeholder = "Write something here",
  options,
  queryKey,
  listQueryKey,
  handleSubmitOnSuccess = async () => { },
  handleChangeSideEffect = (contents) => { },
  acceptOutsideChange = false,
  setAcceptOutsideChange = () => { },
  autoFocus = false,
  onContentChange = null,
  initialContent = null,
}, ref) {
  const quillRef = useRef(null);
  const speechToTextRef = useRef(null);
  const [delta, setDelta] = useState();
  // Tracks the most recently typed content (updated synchronously in handleChange)
  // so `flush()` can fire a save for keystrokes that landed inside the debounce
  // window before the editor unmounts.
  const latestDeltaRef = useRef(null);
  // Tracks the last delta we've handed to the server so `flush()` can skip
  // when there's nothing new to persist.
  const lastSentDeltaRef = useRef(null);
  const debouncedDelta = useDebounce(delta, SAVE_INTERVAL_MS);
  const [isSaving, setIsSaving] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [hasChanged, setHasChanged] = useState(false);
  const hasChangedRef = useRef(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isFormattingTranscript, setIsFormattingTranscript] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');
  const baseTranscriptRef = useRef('');
  /** Memoised Quill modules config (toolbar + image uploader). Stable across re-renders. */
  const modules = useMemo(() => {
    return {
      toolbar: quillEditorToolbar,
      imageUploader: imageUploaderFunction,
    };
  }, []);

  const [quill, setQuill] = useState();
  /** Auth type derived from `options.auth`; controls which backend routes are called. */
  const authType = options.auth === 'user' ? API_AUTH_TYPES.USER : API_AUTH_TYPES.ADVISOR;
  /** API client for Quill document operations (find-or-create, update, submit draft). */
  const quillApi = new ApiRequest(
    API_ROUTES.QUILL_TEXT_EDITOR,
    authType
  );
  /** API client for AI voice-to-text formatting requests. */
  const voiceToTextApi = new ApiRequest(API_ROUTES.VOICE_TO_TEXT, authType);

  const queryClient = useQueryClient();
  const { addToast } = useAppContext();
  // Throttle autosave error toasts so a stuck oversized doc doesn't spam
  // the user every 200ms while they type.
  const lastSaveErrorToastAt = useRef(0);

  const {
    data: document,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryFn: async () => {
      // When onContentChange is provided, skip API and return local content
      if (onContentChange) {
        return { id: 'local', content: initialContent };
      }
      return await quillApi.post({ endpoint: "find_or_create", body: options });
    },
    queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
    refetchOnWindowFocus: false,
  });
  /** Persists the current editor Delta content to the server. */
  const { mutateAsync: mutateSaveAsync } = useMutation(
    (content) =>
      quillApi.patch({
        endpoint: "update",
        body: {
          route: options.route,
          content,
          id: document.id,
          field: options.field // Pass field for routes that need it (like advice_library)
        }
      })
    ,
    {
      onSuccess: (data, savedContent) => {
        // Update cache directly instead of invalidating — avoids refetch
        // race condition where stale server content overwrites active edits
        const normalizedKey = Array.isArray(queryKey) ? queryKey : [queryKey];
        queryClient.setQueryData(normalizedKey, (old) =>
          old ? { ...old, content: savedContent } : old
        );

        if (options.onSuccess) {
          options.onSuccess();
        }
      },
      onError: (error) => {
        console.error('Failed to save document:', error);
        const now = Date.now();
        if (now - lastSaveErrorToastAt.current < 30 * 1000) return;
        lastSaveErrorToastAt.current = now;
        const status = error?.response?.status;
        if (status === 413) {
          addToast({
            type: "error",
            title: "Couldn't save — content too large",
            description:
              "Your last change wasn't saved because the document is too large. Pasted images embed at full size — try removing pasted images or uploading them with the toolbar instead.",
            timeout: 12000,
          });
        } else {
          addToast({
            type: "error",
            title: "Couldn't save your changes",
            description:
              "Your last edit didn't save. Check your connection — refreshing the page will lose unsaved work.",
            timeout: 10000,
          });
        }
      },
    }
  );
  const saveDocumentContentRef = useRef(async () => { });
  saveDocumentContentRef.current = async (content) => {
    await mutateSaveAsync(content);
    lastSentDeltaRef.current = content;
  };
  const autosaveQueueRef = useRef(null);
  if (!autosaveQueueRef.current) {
    autosaveQueueRef.current = createLatestOnlySaveQueue({
      save: (content) => saveDocumentContentRef.current(content),
      onStateChange: setIsSaving,
    });
  }
  const enqueueAutosave = useCallback((content) => (
    autosaveQueueRef.current.enqueue(content)
  ), []);

  /** Submits the current document as a finalized draft, clears the editor, and invalidates related queries. */
  const { mutate: mutateSubmitDraft, isLoading: isSubmiting } = useMutation(
    () =>
      quillApi.patch({
        endpoint: "submit_draft",
        body: { route: options.route, id: document.id },
      }),
    {
      onSuccess: () => {
        // Invalidate and refetch
        handleSubmitOnSuccess(document.id);

        queryClient.invalidateQueries(queryKey);
        queryClient.invalidateQueries(listQueryKey);
        if (quill) {
          quill.setContents()
          quill.setText("");
        }

        // Call the onSuccess callback from options if provided
        if (options.onSuccess) {
          options.onSuccess();
        }
      },
    }
  );

  // Expose focus and flush to parent components. `flush()` drains any
  // keystrokes still sitting in the 200ms debounce window so callers can
  // `await quillRef.current.flush()` before unmounting the editor or
  // invalidating caches — otherwise the pending save is cancelled on
  // unmount and the user's last typing is lost.
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        editor.focus();
      }
    },
    flush: async () => {
      const latest = latestDeltaRef.current;
      if (!latest || latest === lastSentDeltaRef.current) return;

      if (isQuillContentEffectivelyEmpty(latest)) return;

      if (onContentChange) {
        onContentChange(latest);
        return;
      }

      try {
        await enqueueAutosave(latest);
      } catch (err) {
        console.error('Flush save failed:', err);
      }
    },
  }));


  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (quill == null) return;

    if (
      quill?.getContents().ops[0].insert === "\n" &&
      quill?.getContents().ops.length === 1
    )
      return setIsEmpty(true);
    setIsEmpty(false);
  }, [quill?.getContents().ops[0].insert]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (quillRef.current == null) return;
    if (hasChangedRef.current && !acceptOutsideChange) return
    const q = quillRef.current.getEditor();
    q.history.clear();

    // Set contents to document content or clear if empty
    if (document) {
      q.setContents(document.content || { ops: [{ insert: "\n" }] });
    }

    setQuill(q);

    setDelta(q.getContents());
    setAcceptOutsideChange(false)

    // Auto-focus if requested
    if (autoFocus) {
      setTimeout(() => q.focus(), 100);
    }
  }, [isLoading, document]);  // Add document as a dependency


  // Effect for API call
  const [firstRender, setFirstRender] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
      return;
    }

    // Check if content is effectively empty before saving
    if (debouncedDelta && debouncedDelta.ops) {
      if (!isQuillContentEffectivelyEmpty(debouncedDelta)) {
        if (onContentChange) {
          // Browser draft mode: call callback instead of API save
          onContentChange(debouncedDelta);
          setIsSaving(false);
        } else {
          enqueueAutosave(debouncedDelta);
        }
      } else {
        setIsSaving(false);
      }
    }
  }, [debouncedDelta]);




  /**
   * Fires on every Quill content change; marks the editor as dirty and triggers the debounced save.
   */
  const handleChange = () => {
    if (quill == null) return;
    const contents = quill.getContents();

    latestDeltaRef.current = contents;
    setIsSaving(true);
    setDelta(contents);
    handleChangeSideEffect(contents);
    setHasChanged(true)
    hasChangedRef.current = true;
  };

  /**
   * Called with the final (committed) transcript segment; appends it to the running base transcript.
   * @param {string} transcript
   */
  const handleSpeechTranscript = useCallback((transcript) => {
    if (!transcript) return;
    const trimmed = transcript.trim();
    const updatedBase = baseTranscriptRef.current
      ? `${baseTranscriptRef.current} ${trimmed}`.trim()
      : trimmed;
    baseTranscriptRef.current = updatedBase;
    setLiveTranscript(updatedBase);
  }, []);

  /**
   * Called with in-progress (partial) transcript words; updates the live preview without committing to base.
   * @param {string} transcript
   */
  const handlePartialTranscript = useCallback((transcript) => {
    if (!transcript) {
      setLiveTranscript(baseTranscriptRef.current);
      return;
    }
    const combined = baseTranscriptRef.current
      ? `${baseTranscriptRef.current} ${transcript}`.trim()
      : transcript.trim();
    setLiveTranscript(combined);
  }, []);

  /** Resets the transcript state and sets `isRecording` to true when the microphone opens. */
  const handleRecordingStart = useCallback(() => {
    baseTranscriptRef.current = '';
    setLiveTranscript('');
    setIsRecording(true);
  }, []);

  /** Sets `isRecording` to false when the microphone closes. */
  const handleRecordingEnd = useCallback(() => {
    setIsRecording(false);
  }, []);

  /**
   * Stops any active recording, sends the live transcript to the AI formatting endpoint,
   * and inserts the formatted text at the end of the editor content.
   */
  const handleFormatTranscript = useCallback(async () => {
    if (!quill) return;
    const transcriptToFormat = liveTranscript.trim();
    if (!transcriptToFormat) return;

    // Ensure any ongoing recording stops before formatting
    try {
      speechToTextRef.current?.stopRecording?.();
    } catch (err) {
      console.error('Error stopping recording before formatting:', err);
    } finally {
      setIsRecording(false);
    }

    setIsFormattingTranscript(true);
    try {
      const response = await voiceToTextApi.post({
        endpoint: 'format',
        body: { text: transcriptToFormat }
      });

      const formattedText = (response?.formatted_text || transcriptToFormat).trim();

      if (formattedText) {
        const contents = quill.getContents();
        quill.setContents([...contents.ops, { insert: `${formattedText}\n` }]);

        const newContents = quill.getContents();
        setIsSaving(true);
        setDelta(newContents);
        handleChangeSideEffect(newContents);
      }
    } catch (error) {
      console.error('Error formatting transcript:', error);
    } finally {
      setIsFormattingTranscript(false);
      baseTranscriptRef.current = '';
      setLiveTranscript('');
    }
  }, [handleChangeSideEffect, liveTranscript, voiceToTextApi, quill]);

  if (isLoading) return <DefaultLoading />;
  if (isError) return <p>{error?.message || "Something went wrong"}</p>;
  return (
    <div className="rounded-md">
      <div className="  rounded-md flex flex-col justify-center items-center  ">
        <div className="w-full relative">
          {(isSubmiting || isSaving) && (
            <SavingCircle label={isSubmiting ? "Submitting draft" : "Saving changes"} />
          )}
          <ReactQuill
            ref={quillRef}
            modules={modules}
            formats={quillEditorFormats}
            onChange={handleChange}
            defaultValue={document.content || null}
            placeholder={placeholder}
          />
          {(isRecording || liveTranscript) && (
            <div className="absolute top-3 right-3 w-72 bg-white shadow-lg border border-gray-200 rounded-lg p-3 z-10">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600">Live transcription</p>
                {isFormattingTranscript && (
                  <div className="flex items-center space-x-1 text-xs text-gray-500">
                    <AiOutlineLoading3Quarters className="animate-spin h-3 w-3" />
                    <span>Formatting…</span>
                  </div>
                )}
              </div>
              <textarea
                className="w-full bg-gray-50 border border-gray-200 rounded-md p-2 text-sm text-gray-700 resize-none focus:outline-none"
                rows={4}
                value={liveTranscript}
                placeholder="Start speaking to populate transcript…"
                readOnly
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleFormatTranscript}
                  disabled={!liveTranscript.trim() || isFormattingTranscript}
                  className={classNames(
                    "px-3 py-1.5 text-sm font-medium rounded-md transition",
                    (!liveTranscript.trim() || isFormattingTranscript)
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-blue-500 text-white hover:bg-blue-600"
                  )}
                >
                  {isFormattingTranscript ? "Formatting…" : "Format & Insert"}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="w-full flex flex-col space-y-2 mt-2">
          <div className="flex justify-end space-x-2 items-center">
            <StreamingSpeechToText
              ref={speechToTextRef}
              authType={authType}
              onTranscript={handleSpeechTranscript}
              onPartialTranscript={handlePartialTranscript}
              onRecordingStart={handleRecordingStart}
              onRecordingEnd={handleRecordingEnd}
              disabled={isSubmiting || isSaving || isFormattingTranscript}
            />
            {options.is_draft && (
              <button
                disabled={isSubmiting || isSaving || isEmpty || isRecording || isFormattingTranscript}
                className={classNames(
                  "btn-primary btn-md",
                  (isSubmiting || isSaving || isEmpty || isRecording || isFormattingTranscript) && "opacity-50"
                )}
                onClick={() => mutateSubmitDraft()}
              >
                <IoMdSend className="h-5 w-7 text-white" />
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
});

export default QuillTextEditor;
